import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, Flashlight, X } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { Alert, Button, IconButton, Overline } from '@/components/ui';
import { formatCurrency } from '@/utils/formatters';
import type { Product } from '@/services/types';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onDetected: (barcode: string) => void;
    /** Keep scanning after a hit (default). false = close after first code. */
    continuous?: boolean;
    /** Last product added from a scan — shown in the "just scanned" row */
    lastScanned?: Product | null;
}

// Native BarcodeDetector (Chrome/Edge/Android). Typed loosely because lib.dom lacks it.
type Detector = { detect: (src: ImageBitmapSource) => Promise<{ rawValue: string }[]> };
type DetectorCtor = new (opts?: { formats?: string[] }) => Detector;

export const hasBarcodeDetector = () => typeof window !== 'undefined' && 'BarcodeDetector' in window;

const SAME_CODE_DEBOUNCE_MS = 1500;

/**
 * 04b full-screen camera scanner. USB/keyboard scanners don't need this: they type into the search box.
 * Phone: camera + results sheet. Landscape: camera pane + 400px cart panel.
 */
export const BarcodeScannerModal: React.FC<Props> = ({ isOpen, onClose, onDetected, continuous = true, lastScanned }) => {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [error, setError] = useState<string | null>(null);
    const trackRef = useRef<MediaStreamTrack | null>(null);
    const [torchAvailable, setTorchAvailable] = useState(false);
    const [torch, setTorch] = useState(false);
    const { items, calculation } = useAppSelector((s) => s.cart);
    const itemCount = items.reduce((n, i) => n + i.quantity, 0);

    useEffect(() => {
        if (!isOpen) return;
        let stream: MediaStream | null = null;
        let timer: number | undefined;
        let done = false;
        let lastCode = '';
        let lastAt = 0;

        const start = async () => {
            try {
                const Ctor = (window as unknown as { BarcodeDetector: DetectorCtor }).BarcodeDetector;
                const detector = new Ctor({ formats: ['ean_13', 'ean_8', 'code_128', 'upc_a', 'upc_e', 'qr_code'] });
                stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                const track = stream.getVideoTracks()[0] ?? null;
                trackRef.current = track;
                // `torch` is not in lib.dom's MediaTrackCapabilities; only Android Chrome reports it
                setTorchAvailable(!!(track?.getCapabilities?.() as { torch?: boolean } | undefined)?.torch);
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
                const tick = async () => {
                    if (done || !videoRef.current) return;
                    try {
                        const codes = await detector.detect(videoRef.current);
                        const code = codes[0]?.rawValue;
                        if (code && (code !== lastCode || Date.now() - lastAt > SAME_CODE_DEBOUNCE_MS)) {
                            lastCode = code;
                            lastAt = Date.now();
                            onDetected(code);
                            if (!continuous) { done = true; return; }
                        }
                    } catch { /* frame not ready */ }
                    timer = window.setTimeout(tick, 250);
                };
                tick();
            } catch (e) {
                setError(e instanceof Error ? e.message : t('catalog.cameraError'));
            }
        };
        start();

        return () => {
            done = true;
            if (timer) clearTimeout(timer);
            stream?.getTracks().forEach((tr) => tr.stop());
            trackRef.current = null;
            setTorch(false);
            setTorchAvailable(false);
        };
    }, [isOpen, onDetected, continuous, t]);

    const toggleTorch = async () => {
        const next = !torch;
        try {
            await trackRef.current?.applyConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] });
            setTorch(next);
        } catch { setTorchAvailable(false); }
    };

    if (!isOpen) return null;

    const corner = 'absolute h-[34px] w-[34px] border-accent-500 lg:h-11 lg:w-11';

    return createPortal(
        <div className="fixed inset-0 z-[900] flex flex-col bg-primary-950 text-white lg:flex-row">
            <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4 pt-2 lg:gap-4 lg:p-6">
                <div className="flex items-center gap-2.5 px-1">
                    <IconButton variant="onDark" label={t('common.close')} onClick={onClose}><X /></IconButton>
                    <h1 className="flex-1 text-xl font-black lg:text-[22px]">{t('catalog.scanBarcode')}</h1>
                    {torchAvailable && (
                        <button type="button" onClick={toggleTorch} aria-pressed={torch} className={`flex h-9 items-center gap-1.5 rounded-pill px-3.5 text-[13px] font-extrabold ${torch ? 'bg-accent-500 text-ink-onAccent' : 'bg-white/[.12] text-white'}`}>
                            <Flashlight className="h-4 w-4" />{t('catalog.torch')}
                        </button>
                    )}
                </div>
                <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl bg-[#2a1a25]">
                    {error ? (
                        <div className="p-4"><Alert variant="error" message={error} /></div>
                    ) : (
                        <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
                    )}
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="relative h-[150px] w-[260px] lg:h-[240px] lg:w-[440px]">
                            <span className={`${corner} left-0 top-0 rounded-tl-[16px] border-l-4 border-t-4`} />
                            <span className={`${corner} right-0 top-0 rounded-tr-[16px] border-r-4 border-t-4`} />
                            <span className={`${corner} bottom-0 left-0 rounded-bl-[16px] border-b-4 border-l-4`} />
                            <span className={`${corner} bottom-0 right-0 rounded-br-[16px] border-b-4 border-r-4`} />
                            <span className="absolute left-[18px] right-[18px] top-1/2 h-0.5 bg-accent-500 shadow-[0_0_14px_var(--accent)]" />
                        </div>
                    </div>
                    <p className="pointer-events-none absolute inset-x-0 bottom-4 text-center text-[13px] font-bold text-primary-200">{t('catalog.scanHint')}{continuous ? ` · ${t('catalog.continuousScan')}` : ''}</p>
                </div>
            </div>

            <div className="flex shrink-0 flex-col gap-3 rounded-sheet bg-surface-card px-5 pb-[max(26px,env(safe-area-inset-bottom))] pt-5 text-ink-primary lg:w-[400px] lg:rounded-none lg:p-[22px]">
                <Overline tone="muted">{t('catalog.justScanned')}</Overline>
                {lastScanned ? (
                    <div className="flex items-center gap-3 rounded-lg bg-status-success-soft p-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-status-success text-white"><Check className="h-5 w-5" strokeWidth={3} /></span>
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="truncate text-sm font-black">{lastScanned.name}</span>
                            <span className="truncate text-xs font-bold text-success-700">{t('catalog.added')}{lastScanned.barcode ? ` · ${lastScanned.barcode}` : ''}</span>
                        </span>
                        <span className="text-sm font-black tnum">{formatCurrency(lastScanned.price)}</span>
                    </div>
                ) : (
                    <p className="text-sm text-ink-muted">{t('catalog.scanHint')}</p>
                )}
                <div className="hidden lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
                    <p className="pt-1.5 text-[17px] font-black">{t('cart.title')} <span className="text-ink-muted">{itemCount}</span></p>
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {items.map((i) => (
                            <div key={i.compositeId} className="flex justify-between border-b border-line-subtle py-3 text-sm">
                                <span className="truncate">{i.quantity}× {i.name}</span>
                                <span className="font-bold tnum">{formatCurrency((i.unitPrice || 0) * i.quantity)}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex items-end justify-between gap-3">
                    <div className="flex flex-col">
                        <span className="text-xs font-bold text-ink-muted">{t('cart.total')} · {t('cart.itemsCount', { count: itemCount })}</span>
                        <span className="text-2xl font-black tnum text-ink-brand lg:text-[28px]">{calculation ? formatCurrency(calculation.grandTotal) : '—'}</span>
                    </div>
                    <Button variant="primary" size="lg" onClick={onClose} className="lg:hidden">{t('common.done')}</Button>
                </div>
                <Button variant="primary" size="lg" fullWidth onClick={onClose} className="hidden lg:inline-flex">{t('catalog.doneScanning')}</Button>
            </div>
        </div>,
        document.body,
    );
};

export default BarcodeScannerModal;
